import { type CollabManager } from '@databases/collab-state-manager/collab-manager';
import { DATABASE_BE_STREAMING_PARTICIPANT } from '@databases/database-ai-first-creation-state';
import { presenceState } from '@databases/presence';
import { defaultStoreUtils } from '@databases/redux';
import { applyDatabaseDiff } from '@databases/utils/redux/databases/actions';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags';

import {
  getBackendAgentAddedFieldIds,
  isFieldOrderingChangeAdditionOnly
} from '../backend-agent-field-additions.ts';
import { getRenderedBackendAgentFieldIds } from '../backend-agent-field-targets.ts';

export const PROGRESSIVE_FIELD_ADD_REVEAL_DELAY_MS = 700;

const MAX_REVEAL_TICKS_AFTER_INITIAL_FIELD = 4;
const DEFERRED_FIELD_PROJECTION_META_KEY = Symbol.for(
  'databases:deferred-field-projection'
);

type DatabaseTransaction = Parameters<CollabManager['database']['getDiff']>[0];

interface QueuedFieldAddition {
  fieldId: string;
}

export type ProgressiveFieldAddRevealResult =
  | { kind: 'idle' }
  | { activeFieldId: string; kind: 'revealing' };

interface UseProgressiveFieldAddRevealOptions {
  collabManager: CollabManager | undefined;
  contentId: string;
  isPaused: boolean;
  layoutRoot: HTMLElement | null;
  onEligibleBackendAgentFieldAdd?: () => void;
  resetKey: string;
}

function hasTableLayout(layoutRoot: HTMLElement | null): layoutRoot is HTMLElement {
  return Boolean(
    layoutRoot &&
    (layoutRoot.matches('table#layout-table') ||
      layoutRoot.querySelector('table#layout-table'))
  );
}

export function useProgressiveFieldAddReveal({
  collabManager,
  contentId,
  isPaused,
  layoutRoot,
  onEligibleBackendAgentFieldAdd,
  resetKey
}: UseProgressiveFieldAddRevealOptions): ProgressiveFieldAddRevealResult {
  const isDatabaseBEStreamingEnabled = getExperimentValue(
    'databaseBEStreaming',
    'enabled',
    { fireExperimentExposure: false }
  );
  const dispatch = defaultStoreUtils.useDispatch();
  const previousFieldIds = useRef<readonly string[]>([]);
  const projectedFieldIdSet = useRef<ReadonlySet<string>>(new Set());
  const isInitialRevealBatch = useRef(true);
  const revealBatchSize = useRef(1);
  const hasFiredEligibility = useRef(false);
  const queuedFieldAdditionsRef = useRef<readonly QueuedFieldAddition[]>([]);
  const [queuedFieldAdditions, setQueuedFieldAdditions] = useState<
    readonly QueuedFieldAddition[]
  >([]);

  const replaceQueuedFieldAdditions = useCallback(
    (nextQueuedFieldAdditions: readonly QueuedFieldAddition[]) => {
      queuedFieldAdditionsRef.current = nextQueuedFieldAdditions;
      if (nextQueuedFieldAdditions.length === 0) {
        isInitialRevealBatch.current = true;
        revealBatchSize.current = 1;
        hasFiredEligibility.current = false;
      }
      setQueuedFieldAdditions(nextQueuedFieldAdditions);
    },
    []
  );

  const projectFieldAdditions = useCallback(
    (fieldIds: readonly string[]) => {
      if (!collabManager || fieldIds.length === 0) {
        return;
      }

      projectedFieldIdSet.current = new Set([
        ...projectedFieldIdSet.current,
        ...fieldIds
      ]);
      const projectedFieldIds = collabManager.database.fields.filter((fieldId) =>
        projectedFieldIdSet.current.has(fieldId)
      );

      const diffInfo = {
        contentId,
        diff: {
          fieldsOrderingChanged: false,
          entriesOrderingChanged: false,
          changedFieldIds: [...fieldIds],
          changedEntryIds: [],
          threadsChanged: false,
          viewsChanged: false,
          defaultEntryChanged: false,
          othersChanged: false
        },
        collabDatabase: collabManager.database,
        isReadOnly: collabManager.isReadOnly,
        projectedFieldIds
      };
      dispatch(applyDatabaseDiff(diffInfo));
    },
    [collabManager, contentId, dispatch]
  );

  useLayoutEffect(() => {
    queuedFieldAdditionsRef.current = [];
    isInitialRevealBatch.current = true;
    revealBatchSize.current = 1;
    hasFiredEligibility.current = false;
    setQueuedFieldAdditions([]);

    if (!collabManager) {
      previousFieldIds.current = [];
      projectedFieldIdSet.current = new Set();
      return;
    }

    previousFieldIds.current = collabManager.database.fields;
    projectedFieldIdSet.current = new Set(collabManager.database.fields);
    const onAfterTransaction = (transaction: DatabaseTransaction) => {
      const diff = collabManager.database.getDiff(transaction);
      const currentFieldIds = collabManager.database.fields;
      const previousTransactionFieldIds = previousFieldIds.current;
      const addedFieldIds = getBackendAgentAddedFieldIds({
        currentFieldIds,
        previousFieldIds: previousTransactionFieldIds,
        transaction
      });
      previousFieldIds.current = currentFieldIds;
      const addedFieldIdSet = new Set(addedFieldIds);
      const isEligibleAddition =
        hasTableLayout(layoutRoot) &&
        diff.fieldsOrderingChanged &&
        diff.changedFieldIds.every((fieldId) => addedFieldIdSet.has(fieldId)) &&
        isFieldOrderingChangeAdditionOnly({
          addedFieldIds,
          currentFieldIds,
          previousFieldIds: previousTransactionFieldIds
        });

      if (!isEligibleAddition) {
        if (
          queuedFieldAdditionsRef.current.length > 0 &&
          (diff.fieldsOrderingChanged || diff.changedFieldIds.length > 0)
        ) {
          projectFieldAdditions(
            queuedFieldAdditionsRef.current.map(({ fieldId }) => fieldId)
          );
          replaceQueuedFieldAdditions([]);
        }
        return;
      }

      if (!isDatabaseBEStreamingEnabled) {
        replaceQueuedFieldAdditions(addedFieldIds.map((fieldId) => ({ fieldId })));
        return;
      }

      transaction.meta.set(DEFERRED_FIELD_PROJECTION_META_KEY, {
        fieldIds: addedFieldIds,
        deferFieldsOrdering: true
      });
      const currentQueuedFieldAdditions = queuedFieldAdditionsRef.current;
      if (currentQueuedFieldAdditions.length === 0) {
        projectedFieldIdSet.current = new Set(previousTransactionFieldIds);
        isInitialRevealBatch.current = true;
      }
      const nextQueuedFieldAdditions = [
        ...currentQueuedFieldAdditions,
        ...addedFieldIds.map((fieldId) => ({ fieldId }))
      ];
      const remainingFieldCount = Math.max(0, nextQueuedFieldAdditions.length - 1);
      revealBatchSize.current = Math.max(
        revealBatchSize.current,
        Math.ceil(remainingFieldCount / MAX_REVEAL_TICKS_AFTER_INITIAL_FIELD)
      );
      replaceQueuedFieldAdditions(nextQueuedFieldAdditions);
    };

    collabManager.database.doc.on('afterTransaction', onAfterTransaction);
    return () => {
      collabManager.database.doc.off('afterTransaction', onAfterTransaction);
      projectFieldAdditions(
        queuedFieldAdditionsRef.current.map(({ fieldId }) => fieldId)
      );
      queuedFieldAdditionsRef.current = [];
    };
  }, [
    collabManager,
    isDatabaseBEStreamingEnabled,
    layoutRoot,
    projectFieldAdditions,
    replaceQueuedFieldAdditions,
    resetKey
  ]);

  const activeBatchSize = isInitialRevealBatch.current
    ? Math.min(1, queuedFieldAdditions.length)
    : Math.min(revealBatchSize.current, queuedFieldAdditions.length);
  const activeFieldAddition = queuedFieldAdditions[activeBatchSize - 1];

  useLayoutEffect(() => {
    if (!isDatabaseBEStreamingEnabled || !activeFieldAddition || isPaused) {
      return;
    }

    projectFieldAdditions(
      queuedFieldAdditions.slice(0, activeBatchSize).map(({ fieldId }) => fieldId)
    );
  }, [
    activeBatchSize,
    activeFieldAddition,
    isDatabaseBEStreamingEnabled,
    isPaused,
    projectFieldAdditions,
    queuedFieldAdditions
  ]);

  useEffect(() => {
    if (!activeFieldAddition || isPaused || !layoutRoot) {
      return;
    }

    let isPresenceActive = false;
    const fireEligibilityWhenRendered = () => {
      const renderedFieldIds = getRenderedBackendAgentFieldIds(layoutRoot, [
        activeFieldAddition.fieldId
      ]);
      if (!renderedFieldIds.has(activeFieldAddition.fieldId)) {
        return false;
      }

      if (!hasFiredEligibility.current) {
        hasFiredEligibility.current = true;
        onEligibleBackendAgentFieldAdd?.();
      }
      if (isDatabaseBEStreamingEnabled) {
        presenceState.addParticipant(
          { fieldId: activeFieldAddition.fieldId },
          DATABASE_BE_STREAMING_PARTICIPANT
        );
        isPresenceActive = true;
      }
      return true;
    };
    const mutationObserver = new MutationObserver(() => {
      if (fireEligibilityWhenRendered()) {
        mutationObserver.disconnect();
      }
    });

    if (!fireEligibilityWhenRendered()) {
      mutationObserver.observe(layoutRoot, { childList: true, subtree: true });
    }

    if (!isDatabaseBEStreamingEnabled) {
      replaceQueuedFieldAdditions([]);
      return () => mutationObserver.disconnect();
    }

    const timeout = setTimeout(() => {
      isInitialRevealBatch.current = false;
      replaceQueuedFieldAdditions(queuedFieldAdditionsRef.current.slice(activeBatchSize));
    }, PROGRESSIVE_FIELD_ADD_REVEAL_DELAY_MS);

    return () => {
      mutationObserver.disconnect();
      clearTimeout(timeout);
      if (isPresenceActive) {
        presenceState.removeParticipant(DATABASE_BE_STREAMING_PARTICIPANT);
      }
    };
  }, [
    activeBatchSize,
    activeFieldAddition,
    isDatabaseBEStreamingEnabled,
    isPaused,
    layoutRoot,
    onEligibleBackendAgentFieldAdd,
    projectFieldAdditions,
    replaceQueuedFieldAdditions
  ]);

  return isDatabaseBEStreamingEnabled && activeFieldAddition && !isPaused
    ? { activeFieldId: activeFieldAddition.fieldId, kind: 'revealing' }
    : { kind: 'idle' };
}
